<script lang="ts">
import Password, { PasswordScore } from 'vue-password-strength-meter';
import { computed, onMounted, shallowRef } from 'vue';
import { store as vuex } from '@/store/store';
import { VTextField } from 'vuetify/lib';
import { useEncrypterService } from '@/modules/common/services/encrypter-service';
import { useRules } from '@/composables/vuetify-validation';
import i18n from '@/localisation/i18n';
import { setInputFocus } from '@/utils/helpers/input-form';
import axios from 'axios';
import { LoginSalt } from '@/utils/helpers/rest';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { errorString } from '@/utils/helpers/rest-response';

const MIN_PASSWORD_SCORE: PasswordScore = 3;
</script>

<script setup lang="ts">
const emit = defineEmits<{ (event: 'close-modal'): void }>();

const snackbar = useSnackbarManager();
const encryptionService = useEncrypterService();
const symmetricEncrypter = computed(() => encryptionService.getSymmetricEncrypter());

const cmpCurrentPassword = shallowRef<null | HTMLFormElement>(null);
const cmpLoginPassword = shallowRef<null | typeof VTextField>(null);
const cmpLoginPasswordAgain = shallowRef<null | typeof VTextField>(null);

const serviceErrors = shallowRef('');
const currentPassword = shallowRef('');
const loginPassword = shallowRef('');
const loginPasswordAgain = shallowRef('');
const isProcessing = shallowRef(false);
const revealPassword = shallowRef(false);
const revealPasswordAgain = shallowRef(false);
const passwordScore = shallowRef<PasswordScore>(0);

const passwordFeedback = shallowRef<null | {
  suggestions: string[];
  warning: string;
}>(null);
const messageType = computed(() => (passwordFeedback.value?.warning ? 'error' : 'info'));

const { rules, validate, isSubmitted, validateAndSubmit } = useRules<{
  currentPassword: string;
  loginPassword: string;
  loginPasswordAgain: string;
}>({
  currentPassword: {
    rules: [(v) => !!v || i18n.t('formfield.IsRequired').toString()],
  },
  loginPassword: {
    rules: [
      (v) => !!v || `${i18n.t('loginPassword')} ${i18n.t('formfield.IsRequired')}`,
      // password strength check is a responsibility of the client
      // the server only sees the encrypted password
      () =>
        !isSubmitted.value ||
        passwordScore.value >= MIN_PASSWORD_SCORE ||
        i18n.t('errPasswordTooSimple').toString(),
    ],
  },
  loginPasswordAgain: {
    rules: [
      (v) => !!v || `${i18n.t('loginPasswordAgain')} ${i18n.t('formfield.IsRequired')}`,
      (v) =>
        !isSubmitted.value ||
        v === loginPassword.value ||
        i18n.t('errPasswordsNotTheSame').toString(),
    ],
  },
});

function closeModalDialog(): void {
  emit('close-modal');
}

async function onSubmit() {
  await validateAndSubmit(async () => {
    if (isProcessing.value) return;
    isProcessing.value = true;

    try {
      // get salt (for entered email address) from the server
      let salt: Buffer;
      try {
        const response = await axios.get(
          `/api/1/login-salt/${vuex.state.loginState!.user?.emailAddress}`
        );
        salt = Buffer.from((response.data as LoginSalt).salt, 'base64');
      } catch {
        setInputFocus(cmpLoginPassword.value);
        serviceErrors.value = i18n.t('login-password-not-available') as string;
        return;
      }

      // hash user input for current password for the server to check
      let oldHashedPassword: string;
      try {
        oldHashedPassword = await symmetricEncrypter.value.hashPassword(
          currentPassword.value,
          salt
        );
      } catch {
        setInputFocus(cmpCurrentPassword.value);
        serviceErrors.value = i18n.t('login-password-not-available') as string;
        return;
      }

      // re-encrypt private key with the new password
      let hashedPassword: string;
      try {
        // hash the *new* password with the *fixed* salt for the server
        hashedPassword = await symmetricEncrypter.value.hashPassword(loginPassword.value, salt);
      } catch {
        setInputFocus(cmpLoginPassword.value);
        serviceErrors.value = i18n.t('login-password-not-available') as string;
        return;
      }

      // submit hashed *old* password and hashed *new* password to the server
      try {
        await vuex.dispatch('changeLoginPassword', { oldHashedPassword, hashedPassword });

        snackbar.confirm(i18n.t('login-password-changed') as string);
        closeModalDialog();
      } catch (e) {
        serviceErrors.value = errorString(e);
        setInputFocus(cmpLoginPassword.value);
      }
    } finally {
      isProcessing.value = false;
    }
  });
  if (rules.currentPassword.errorMessages.length) {
    return; // no focus, keeping the old logic
  }
  if (rules.loginPassword.errorMessages.length) {
    return setInputFocus(cmpLoginPassword.value);
  }
  if (rules.loginPasswordAgain.errorMessages.length) {
    return setInputFocus(cmpLoginPasswordAgain.value);
  }
}

onMounted(() => setInputFocus(cmpCurrentPassword.value));
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="800px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
  >
    <VForm novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{ $t('changePassword.title') }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer>
            <VRow>
              <VCol>
                <VTextField
                  :ref="(r) => (cmpCurrentPassword = r)"
                  v-model="currentPassword"
                  :append-icon="revealPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="current-password"
                  autofocus
                  counter
                  :hint="i18n.t('login-it-is-you')"
                  :label="$t('loginCurrentPassword')"
                  required
                  :type="revealPassword ? 'text' : 'password'"
                  v-bind="rules.currentPassword"
                  @blur="validate('currentPassword')"
                  @click:append="revealPassword = !revealPassword"
                >
                </VTextField>
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="12">
                <VTextField
                  :ref="(r) => (cmpLoginPassword = r)"
                  v-model="loginPassword"
                  :append-icon="revealPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="new-password"
                  counter
                  :label="$t('loginPassword')"
                  required
                  :type="revealPassword ? 'text' : 'password'"
                  v-bind="rules.loginPassword"
                  @click:append="revealPassword = !revealPassword"
                ></VTextField>
              </VCol>
              <VCol cols="12">
                <VTextField
                  :ref="(r) => (cmpLoginPasswordAgain = r)"
                  v-model="loginPasswordAgain"
                  :append-icon="revealPasswordAgain ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="new-password"
                  counter
                  :label="$t('loginPasswordAgain')"
                  required
                  :type="revealPasswordAgain ? 'text' : 'password'"
                  v-bind="rules.loginPasswordAgain"
                  @click:append="revealPasswordAgain = !revealPasswordAgain"
                >
                </VTextField>
              </VCol>
              <VCol cols="12">
                <label>Password strength</label>
                <Password
                  v-model="loginPassword"
                  :strength-meter-only="true"
                  @feedback="(feedback) => (passwordFeedback = feedback)"
                  @score="(score) => (passwordScore = score)"
                />
              </VCol>
              <VCol cols="12">
                <div class="alert-wrapper">
                  <VAlert v-if="serviceErrors !== ''" dense type="error">{{
                    serviceErrors
                  }}</VAlert>
                  <VAlert v-else-if="passwordFeedback" dense :type="messageType">
                    <div
                      v-if="passwordFeedback.suggestions.length"
                      class="d-flex flex-column"
                      :color="messageType"
                      dense
                    >
                      <div v-for="suggestion in passwordFeedback.suggestions" :key="suggestion">
                        {{ $t(suggestion) }}
                      </div>
                    </div>
                    <span v-if="passwordFeedback.warning">{{ $t(passwordFeedback.warning) }}</span>
                  </VAlert>
                  <VAlert v-else-if="passwordScore >= MIN_PASSWORD_SCORE" dense type="info">
                    {{ $t('goodPassword.description') }}
                  </VAlert>
                </div>
              </VCol>
            </VRow>
          </VContainer>
        </VCardText>
        <VCardActions>
          <VBtn class="d-none d-sm-flex" color="secondary" text @click="closeModalDialog">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer />
          <VBtn
            color="primary"
            :disabled="isProcessing"
            :loading="isProcessing"
            min-width="120"
            type="submit"
            @click="onSubmit"
          >
            {{ $t('saveButton') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
/* Overwrite component's default */
.Password {
  max-width: 100%;
}

/* Alert content can have 1, 2 or 3 lines of text; applying a min-height minimises layout shifts */
.alert-wrapper {
  min-height: 82px;
}
</style>
